import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFulfillmentsData, loadFulfillmentBatchForPrinting, exportFulfillmentBatchCSV, clearFulfillmentPrintDetails, exportFulfillmentSummaryCSV, fetchTopFulfillmentSkusData } from './fulfillmentsLogSlice';

// mui components
import {
    Typography,
    FormControl,
    Pagination,
    IconButton,
    Tooltip,
    Card,
    Button,
    Select,
    MenuItem,
    Stack,
    Grid
} from "@mui/material";
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InventoryIcon from '@mui/icons-material/Inventory';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import Printer from '../../../pages/General/Printer'
import PageSearch from '../../../pages/General/PageSearch'
import {
    printBulkLabels, 
} from '../../../pages/Orders-new/ordersSlice';
// Project Components
import FulfillmentsLogFilters from './FulfillmentsLogFilters';
import NorowsData from '../../../pages/General/TrackingDrawer'; // Fallback mapping match

// Custom focus styles from reference setup
const enterpriseControlInputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': { borderColor: '#CBD5E1' },
        '&.Mui-focused': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiSelect-select': {
        padding: '8px 14px !important',
        fontSize: '13px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const utilityIconStyles = (isActive) => ({
    color: '#1976d2',
    padding: '8px',
    borderRadius: '10px',
    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
    border: isActive ? '1px solid rgba(25, 118, 210, 0.2)' : '1px solid #E2E8F0',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(241, 245, 249, 0.8)',
        borderColor: isActive ? 'rgba(25, 118, 210, 0.3)' : '#CBD5E1',
        transform: 'translateY(-1px)'
    },
    '&:active': { transform: 'translateY(0)' }
});

const FulfillmentsReportPage = () => {
    const dispatch = useDispatch();
    const fulfillments = useSelector(state => state.fulfillmentsLog);
    const orders = useSelector(state => state.ordersNew);
    const profile = useSelector(state => state.auth);
    const currentUserId = profile?.profile?.id || 3;
    const [printUrl, setPrintUrl] = useState("")
    const [isPrinted, setIsPrinted] = useState(true)
    // Extracted dynamic state vectors for the top cards metric rendering pipelines
    const topSkus = useSelector(state => state.fulfillmentsLog?.topSkus || []);
    const topSkusLoading = useSelector(state => state.fulfillmentsLog?.topSkusLoading || false);
    const [showFilter, setShowFilter] = useState(false);
    const [pageNumber, setPageNumber] = useState("")
    // ─── EXTRACT DOWNLOADING TRACE PARAMETER FROM REDUX ───
    const isBatchDownloading = fulfillments?.batchDownloading || false;
    const [selectedDates, setSelectedDates] = useState([]);
    const [meta, setMeta] = useState({
        page: 1,
        per_page: 10,
        start_date: new Date(new Date().setDate(new Date().getDate() - 10)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        sorting: JSON.stringify([{ field: 'log_date', sort: 'desc' }])
    });

    useEffect(() => {
        // Clear selection models between page/filter adjustments to avoid out-of-bounds mismatches
        setSelectedDates([]);
        dispatch(fetchFulfillmentsData(meta));
    }, [dispatch, meta]);
    useEffect(() => {
        dispatch(fetchTopFulfillmentSkusData(meta));
    }, [dispatch, meta.start_date, meta.end_date]); // 🚀 Only updates when the actual date window filter parameters change

    // Ensure this selector sits at the top of your component alongside your others
    const fulfillmentsPrintState = useSelector(state => state.fulfillmentsLog?.batchPrintDetails || { data: null, loading: false });

    // ─── FULLY UPDATED RELATIONAL SQL USEEFFECT FOR PRINT LOADING & CLEARING ───
    useEffect(() => {
        const details = fulfillmentsPrintState;

        // 1. Check if loading is finished and data exists
        if (!details.loading && details.data) {
            let labels = [];

            // 2. Convert everything to an array to handle single objects or bulk listings consistently
            const shipments = Array.isArray(details.data) ? details.data : [details.data];

            // 3. Loop through all table rows (extracting flat columns from the shipment model schema)
            shipments.forEach((shipmentData) => {
                // Priority extraction matching your SQL table column definitions
                const url = shipmentData?.carrier_label_url || 
                            shipmentData?.label_url || 
                            shipmentData?.direct_label_url ||
                            shipmentData?.detachable_label_url ||
                            shipmentData?.detachable_direct_label_url;

                // Handle SQL tinyint/boolean void checks safely
                const isVoided = shipmentData?.is_voided === 1 || 
                                 shipmentData?.is_voided === true || 
                                 shipmentData?.shipment_status_category === "Voided";

                if (url && url !== "" && !isVoided) {
                    if (!labels.includes(url)) {
                        labels.push(url);
                    }
                }
            });

            // 4. Trigger Printing via your <Printer /> component props
            if (labels.length === 1) {
                setPrintUrl(labels[0]); 
                setIsPrinted(false); 
                dispatch(clearFulfillmentPrintDetails()); // Clean up loading states immediately
            }else if (labels.length > 1) {
                dispatch(printBulkLabels(labels, currentUserId));
            } else {
                dispatch(clearFulfillmentPrintDetails());
            }
        }
    }, [fulfillmentsPrintState.data, fulfillmentsPrintState.loading, dispatch]);
    useEffect(() => {
        if (orders.bulkLabel && orders.bulkLabel.filename && orders.bulkLabel.filename !== "" && orders.bulkLabel.is_shown === false) {
            setPrintUrl(process.env.REACT_APP_LABELINGCHIMP_LABEL_API_DOMAIN + "/merge-pdf?filename=" + orders.bulkLabel.filename)
            setIsPrinted(false)
        }
    }, [orders.bulkLabel])

    // ─── 2. BULK EXPORT EVENT PIPELINE DISPATCHER ───
    const handleBulkExportCSV = () => {
        if (selectedDates.length === 0) return;
        // Join all checked string arrays into a singular comma-separated request parameters payload
        const datesPayloadString = selectedDates.join(',');
        dispatch(exportFulfillmentBatchCSV(currentUserId, datesPayloadString, () => {
            setSelectedDates([]);
        }));
    };

    const onSortChange = (currentSorting) => {
        setMeta({
            ...meta,
            sorting: JSON.stringify(currentSorting)
        });
    };

    const onChangePerPage = (event) => {
        setPageNumber('')
        setMeta({
            ...meta,
            page: 1,
            per_page: event.target.value
        });
    };

    const onChangePagination = (event, page) => {
        setPageNumber('')
        setMeta({
            ...meta,
            page: page
        });
    };

    const onInputChangePagination = (page) => {
        setPageNumber(page)
        let pagenumber = ''
        if (page == '') {
            pagenumber = 1
        } else {
            pagenumber = page
        }
        setMeta({
            ...meta,
            ...{
                page: pagenumber
            }
        });
    }

    const handleFilterToggle = () => {
        setShowFilter(!showFilter);
    };

    const handleResetFilters = () => {
        setPageNumber("")
        setMeta({
            page: 1,
            per_page: 10,
            start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            sorting: JSON.stringify([{ field: 'log_date', sort: 'desc' }])
        });
    };

    const columns = [
        {
            field: 'raw_date',
            headerName: 'Date',
            headerClassName: 'super-app-theme--header',
            minWidth: 140,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                if (!params.value) return "-";
                
                // Parse '2026-06-30' string context smoothly
                const dateParts = params.value.split('-');
                if (dateParts.length !== 3) return params.value;
                
                // Construct a localized presentation date object instance safely
                const localDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                
                // Formats directly into 'Jun 30' profile format right on the client side
                const formatted = localDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: '2-digit'
                });
                
                return (
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {formatted}
                    </Typography>
                );
            }
        },
        {
            field: 'orders_count',
            headerName: 'Orders',
            headerClassName: 'super-app-theme--header',
            minWidth: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'labels_count',
            headerName: 'Labels Printed',
            headerClassName: 'super-app-theme--header',
            minWidth: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A8A' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'packed_count',
            headerName: 'Labels Packed',
            headerClassName: 'super-app-theme--header',
            minWidth: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#16A34A' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'units_shipped_count',
            headerName: 'Units Shipped',
            headerClassName: 'super-app-theme--header',
            minWidth: 140,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#7C3AED' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 240,
            sortable: false,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
                    <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<FileDownloadIcon sx={{ fontSize: '14px' }} />}
                        onClick={() => dispatch(exportFulfillmentBatchCSV(currentUserId, params.row.raw_date))}
                        
                        // ─── DISABLE BUTTON TO PREVENT CLICK FLOODS WHILE RUNNING ───
                        disabled={isBatchDownloading || fulfillments?.batchPrintDetails?.loading}
                        
                        sx={{ 
                            textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '12px', height: '30px',
                            borderColor: '#E2E8F0', color: '#475569', '&:hover': { backgroundColor: '#F8FAFC', borderColor: '#CBD5E1' }
                        }}
                    >
                        {isBatchDownloading ? 'Processing...' : 'Download'}
                    </Button>
                    <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<LocalPrintshopIcon sx={{ fontSize: '14px' }} />}
                        onClick={() => {
                            setPrintUrl(""); 
                            setIsPrinted(true);
                            dispatch(loadFulfillmentBatchForPrinting(currentUserId, params.row.raw_date));
                        }}
                        
                        // ─── DISABLE PRINT BUTTON ACCORDINGLY DURING EXPORT STREAMS ───
                        disabled={isBatchDownloading || fulfillments?.batchPrintDetails?.loading}
                        
                        sx={{ 
                            textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '12px', height: '30px',
                            borderColor: '#1976d2', color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                        }}
                    >
                        Print
                    </Button>
                </Box>
            )
        }
    ];

    function CustomTotalRows() {
        // ─── EXTRACTS THE ACTIVE ROWS ARRAY LENGTH INSTEAD OF THE PARENT METADATA OBJECT ───
        const rowsArray = Array.isArray(fulfillments?.data) 
            ? fulfillments.data 
            : (fulfillments?.data?.data || []);
            
        const currentCount = rowsArray.length || 0; 
        
        // Target the correct metadata location wrapper
        const totalCount = fulfillments?.meta?.total || fulfillments?.data?.total || 0; 

        return (
            <Box 
                className="custom-total-rows" 
                sx={{ 
                    display: 'inline-flex',  
                    alignItems: 'center', 
                    py: 0.6, 
                    px: 1.5, 
                    borderRadius: '8px', 
                    backgroundColor: '#F1F5F9',  
                    border: '1px solid #E2E8F0' 
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#475569' }}> 
                    Showing <strong style={{ color: '#0F172A', fontWeight: 700 }}>{currentCount}</strong> of <strong style={{ color: '#0F172A', fontWeight: 700 }}>{totalCount}</strong> 
                </Typography>
            </Box>
        );
    }

    function CustomPagesizeoptions() {
        return (
            <Box className="custom-page-size-options" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Show</Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 80, ...enterpriseControlInputStyles }}>
                        <Select value={meta?.per_page || 10} onChange={onChangePerPage}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>entries</Typography>
                </Stack>
            </Box>
        );
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, width: '100%', boxSizing: 'border-box' }}>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {

        return (
            <Box
                sx={{
                    p: 1.5,
                    bgcolor: '#F8FAFC', 
                    borderTop: '1px solid #E2E8F0', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end', 
                    
                    width: '100%', 
                    boxSizing: 'border-box', 
                    '& .MuiPaginationItem-root': {
                        fontSize: '13px', 
                        fontWeight: 600, 
                        borderRadius: '8px', 
                        '&.Mui-selected': {
                            backgroundColor: '#1976d2', 
                            color: '#FFFFFF', 
                            '&:hover': { backgroundColor: '#115293' } 
                        }
                    }
                }}
            >
                <Pagination
                    color="primary" 
                    shape="circular" 
                    page={fulfillments && fulfillments.meta && fulfillments.meta.data && fulfillments.meta.data.current_page} 
                    count={fulfillments && fulfillments.meta && fulfillments.meta.data && fulfillments.meta.data.last_page} 
                    onChange={onChangePagination} 
                />
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <PageSearch 
                        pagenumber={pageNumber} 
                        lastPage={fulfillments?.meta?.data?.last_page || 1} 
                        getpagenumber={onInputChangePagination} 
                    />
                </Box>
            </Box>
        );
    }
    const lineColors = ['#1976d2', '#7C3AED', '#10B981', '#F59E0B', '#EF4444'];
    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box'}}>
            <Box className="sccardhead" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.5px', margin: 0 }}>
                        Fulfillments Report
                    </Typography>
                </Stack>
                <Box className="sccardhead" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                    {/* RIGHT HEADER ALIGNMENT CONTAINER FOR THE SEVERAL EXPORT OPTION BUTTONS */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 0, m: 0 }}>
                        
                        {/* BUTTON A: NEW CONTINUOUS MAIN DATAGRID FILTER DATA GENERATOR ROW BUTTON */}
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FileDownloadIcon sx={{ fontSize: '15px' }} />}
                            onClick={() => dispatch(exportFulfillmentSummaryCSV(meta))}
                            disabled={isBatchDownloading}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: '13px',
                                height: '36px',
                                px: 2,
                                borderColor: '#CBD5E1',
                                color: '#334155',
                                backgroundColor: '#FFFFFF',
                                '&:hover': { backgroundColor: '#F8FAFC', borderColor: '#94A3B8' }
                            }}
                        >
                            Export Summary
                        </Button>

                        {/* BUTTON B: DYNAMIC BULK DATES MONGODB PIECES SUB-DOCUMENT EXTRACTOR ROW BUTTON */}
                        {selectedDates.length > 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleBulkExportCSV}
                                disabled={isBatchDownloading}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    height: '36px',
                                    px: 2,
                                    backgroundColor: '#16A34A',
                                    '&:hover': { backgroundColor: '#15803D' }
                                }}
                            >
                                Bulk Export ({selectedDates.length})
                            </Button>
                        )}
                        
                        <Tooltip title="Filter Panels" placement="top">
                            <IconButton sx={utilityIconStyles(showFilter)} onClick={handleFilterToggle}>
                                <FilterAltOutlinedIcon sx={{ fontSize: '20px' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            <FulfillmentsLogFilters 
                showFilter={showFilter} 
                meta={meta} 
                onFilterChange={(updated) => setMeta(prev => ({ ...prev, ...updated }))} 
                onReset={handleResetFilters} 
            />
            <Printer isPrinted={isPrinted} setIsPrinted={setIsPrinted} print_url={printUrl} />    
            {/* ─── NEW SECTION: TOP 5 FULFILLMENT SKUS CARDS GRID ─── */}
            <Box sx={{ mb: 4, mt: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', mb: 2, fontSize: '14px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: '18px', color: '#475569' }} /> Top 5 Shipped SKUs by Volume
                </Typography>
                {topSkusLoading ? (
                    <Box sx={{ width: '100%', py: 2 }}><LinearProgress /></Box>
                ) : (
                    <Grid container spacing={2}>
                        {topSkus.length === 0 ? (
                            <Grid item xs={12}>
                                <Card sx={{ p: 2, textAlign: 'center', color: '#64748B', border: '1px solid #E2E8F0', borderRadius: '12px', boxShadow: 'none' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>No SKU adjustments recorded in this window.</Typography>
                                </Card>
                            </Grid>
                        ) : (
                            topSkus.map((item, index) => (
                                <Grid item xs={12} sm={6} md={2.4} key={item.sku}>
                                    <Card sx={{ p: 2.5, backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)', position: 'relative', overflow: 'hidden' }}>
                                        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', backgroundColor: lineColors[index % lineColors.length] }} />
                                        
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A', fontFamily: 'monospace', fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mb: 1.5 }} title={item.sku}>
                                                {item.sku}
                                            </Typography>
                                            <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', backgroundColor: index === 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(100, 116, 139, 0.08)', color: index === 0 ? '#1976d2' : '#475569' }}>
                                                <Typography variant="caption" sx={{ fontSize: '10.5px', fontWeight: 700 }}>
                                                    Top {index + 1}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                       

                                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1, letterSpacing: '-0.5px' }}>
                                                {(parseInt(item.total_quantity) || 0).toLocaleString()}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '12px' }}>
                                                units
                                            </Typography>
                                        </Stack>
                                    </Card>
                                </Grid>
                            ))
                        )}
                    </Grid>
                )}
            </Box>
            <Card sx={{ width: '100%', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)', overflow: 'hidden' }}>
                <Box sx={{ width: '100%', backgroundColor: '#FFFFFF' }}>
                    <DataGrid
                        sx={{
                            border: 'none',
                            backgroundColor: '#FFFFFF',
                            overflow: 'hidden',
                            '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                            '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                            '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' }, '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.04)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } } },
                            '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                            '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                        }}
                        getRowId={(row) => row.raw_date}
                        rows={(fulfillments?.data && Array.isArray(fulfillments.data)) ? fulfillments.data : (fulfillments?.data?.data || [])}
                        columns={columns}
                        autoHeight={true}
                        disableColumnFilter={true}
                        disableColumnMenu={true}
                        sortingMode="server"
                        onSortModelChange={(newSortModel) => onSortChange(newSortModel)}
                        sortModel={meta.sorting ? JSON.parse(meta.sorting) : []}
                        disableRowSelectionOnClick
                        
                        // ─── 4. ACTIVATE CHECKBOX SELECTION PROPERTIES ON DATAGRID ───
                        checkboxSelection={true}
                        rowSelectionModel={selectedDates}
                        onRowSelectionModelChange={(newSelection) => {
                            setSelectedDates(newSelection);
                        }}
                        
                        slots={{
                            loadingOverlay: LinearProgress,
                            footer: CustomPagination,
                            toolbar: CustomToolbar,
                            NorowsData
                        }}
                        loading={fulfillments?.loading || fulfillments?.batchPrintDetails?.loading || isBatchDownloading}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default FulfillmentsReportPage;